import React, {useState} from 'react'
// eslint-disable-next-line no-restricted-imports
import {css} from '@emotion/css'
import {
  ChildPolicy,
  FAQResponse
} from '@findhotel/sapi/dist/types/packages/core/src/types'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {ANIMATION_TYPES, Overlay} from '@vio/atlas/Overlay'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Brand} from '@vio/core/src/brand/types'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {RoomWithoutOffers} from '@vio/core/src/room/types/room'
import {RoomSplitType} from '@vio/core/src/room/types/RoomConfiguration'
import {ImageProvider} from '@vio/core/src/staticContent/types/ImageProvider'

import {RoomDetailsMobileHeader} from './RoomDetailsMobileHeader'
import {RoomDetailsOverlayContent} from './RoomDetailsOverlayContent'
import {RoomDetailsOverlayImage} from './RoomDetailsOverlayImage'
import {
  ContentColumn,
  DESKTOP_MAX_WIDTH,
  DESKTOP_NO_IMAGE_WIDTH,
  DesktopContentWrapper,
  DesktopNoImageContentWrapper,
  MobileImageWrapper,
  ScrollRegion,
  StyledPopUp
} from './styles'

import type {RoomDealDetailsProps} from './RoomDealDetails'

interface Props {
  isOpen: boolean
  room: RoomWithoutOffers
  /** Name shown to the guest, from `useRoomDisplayName`. Defaults to `room.name`. */
  roomDisplayName?: string
  imageProvider: ImageProvider
  onClose?: () => void
  footerSlot?: React.ReactNode
  userCountry: string
  brand: Brand
  faqData?: FAQResponse
  roomsSplit?: RoomSplitType[]
  childPolicy?: ChildPolicy
  offersSectionSlot?: React.ReactNode
  roomDealDetails?: RoomDealDetailsProps
  /** When true, the overlay content scrolls to the deal details section on open */
  scrollToDealDetails?: boolean
}

interface RoomDetailsOverlayLayoutProps
  extends Pick<
    Props,
    | 'childPolicy'
    | 'room'
    | 'roomDisplayName'
    | 'userCountry'
    | 'imageProvider'
    | 'faqData'
    | 'roomsSplit'
    | 'footerSlot'
    | 'offersSectionSlot'
    | 'roomDealDetails'
    | 'scrollToDealDetails'
  > {
  isMobile?: boolean
  onBackButtonClick?: () => void
  hasImages?: boolean
}

interface LayoutProps {
  imageSection: React.ReactNode
  footer: React.ReactNode
  children: React.ReactNode
}

const MobileLayout = ({imageSection, footer, children}: LayoutProps) => {
  return (
    <ContentColumn>
      <ScrollRegion>
        {imageSection && (
          <MobileImageWrapper>{imageSection}</MobileImageWrapper>
        )}
        {children}
      </ScrollRegion>
      {footer}
    </ContentColumn>
  )
}

const DesktopLayout = ({imageSection, footer, children}: LayoutProps) => {
  if (!imageSection) {
    return (
      <Box display="flex" flexDirection="row" height="100%">
        <DesktopNoImageContentWrapper>
          <ScrollRegion>{children}</ScrollRegion>
          {footer}
        </DesktopNoImageContentWrapper>
      </Box>
    )
  }
  return (
    <Box display="flex" flexDirection="row" height="100%">
      <Box flex="1" minWidth="0">
        {imageSection}
      </Box>
      <Divider orientation="vertical" />
      <DesktopContentWrapper>
        <ScrollRegion>{children}</ScrollRegion>
        {footer}
      </DesktopContentWrapper>
    </Box>
  )
}

const RoomDetailsOverlayLayout = ({
  childPolicy,
  room,
  roomDisplayName,
  userCountry,
  imageProvider,
  onBackButtonClick,
  faqData,
  roomsSplit,
  footerSlot,
  offersSectionSlot,
  roomDealDetails,
  scrollToDealDetails,
  hasImages
}: RoomDetailsOverlayLayoutProps) => {
  const [isRoomNameInView, setIsRoomNameInView] = useState(true)
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const isMobileOrTablet = isMobile || isDesktopXs
  const Layout = isMobileOrTablet ? MobileLayout : DesktopLayout

  if (!room) return null

  // Checkout passes deal details and titles the overlay "Details"; AP shows the room name.
  const isDetailsVariant = !!roomDealDetails

  return (
    <Layout
      imageSection={
        hasImages ? (
          <RoomDetailsOverlayImage
            images={room.images}
            imageProvider={imageProvider}
          />
        ) : null
      }
      footer={footerSlot}
    >
      {isMobile && (
        <RoomDetailsMobileHeader
          title={
            isDetailsVariant ? (
              <FormattedMessageWrapper id="details" defaultMessage="Details" />
            ) : (
              (roomDisplayName ?? room.name)
            )
          }
          isRoomNameInView={isRoomNameInView}
          onBackButtonClick={onBackButtonClick}
          isStatic={!hasImages}
        />
      )}
      <RoomDetailsOverlayContent
        childPolicy={childPolicy}
        room={room}
        roomDisplayName={roomDisplayName}
        userCountry={userCountry}
        onRoomNameInView={setIsRoomNameInView}
        faqData={faqData}
        roomsSplit={roomsSplit}
        offersSectionSlot={offersSectionSlot}
        roomDealDetails={roomDealDetails}
        scrollToDealDetails={scrollToDealDetails}
      />
    </Layout>
  )
}

export const RoomDetailsOverlay = ({
  isOpen,
  room,
  roomDisplayName,
  userCountry,
  imageProvider,
  onClose,
  faqData,
  roomsSplit,
  childPolicy,
  footerSlot,
  offersSectionSlot,
  roomDealDetails,
  scrollToDealDetails
}: Props) => {
  const {isMobile} = useDeviceLayout()
  // Checkout passes deal details and titles the overlay "Details"; AP shows "Room details".
  const isDetailsVariant = !!roomDealDetails
  const hasImages = Boolean(room?.images?.length)
  return (
    <>
      {isMobile ? (
        <Overlay
          animationType={ANIMATION_TYPES.NONE}
          enableContentBackgroundHack
          isOpen={isOpen}
          shadeBackground={false}
          enableBodyScrollLock={false}
          /**
           * This + enableBodyScrollLock=false is a workaround to fix horizontal rooms-offers
           * scrolling on the overlay when user is at the bottom of it's scroll container.
           * Active bodyscroll seems to be blocking it.
           */
          customStyles={css`
            overscroll-behavior-y: none;
          `}
          containerStyles={{height: '100dvh'}}
          onClose={onClose}
        >
          <RoomDetailsOverlayLayout
            imageProvider={imageProvider}
            room={room}
            roomDisplayName={roomDisplayName}
            userCountry={userCountry}
            onBackButtonClick={onClose}
            childPolicy={childPolicy}
            faqData={faqData}
            roomsSplit={roomsSplit}
            footerSlot={footerSlot}
            offersSectionSlot={offersSectionSlot}
            roomDealDetails={roomDealDetails}
            scrollToDealDetails={scrollToDealDetails}
            hasImages={hasImages}
          />
        </Overlay>
      ) : (
        <StyledPopUp
          noPaddings
          isOpen={isOpen}
          closeAction={onClose}
          navBarProps={{
            centerContent: isDetailsVariant ? (
              <FormattedMessageWrapper id="details" defaultMessage="Details" />
            ) : (
              <FormattedMessageWrapper
                id="roomDetails"
                defaultMessage="Room details"
              />
            ),
            withDivider: true
          }}
          width={hasImages ? DESKTOP_MAX_WIDTH : DESKTOP_NO_IMAGE_WIDTH}
        >
          <RoomDetailsOverlayLayout
            imageProvider={imageProvider}
            room={room}
            roomDisplayName={roomDisplayName}
            userCountry={userCountry}
            childPolicy={childPolicy}
            faqData={faqData}
            roomsSplit={roomsSplit}
            footerSlot={footerSlot}
            offersSectionSlot={offersSectionSlot}
            roomDealDetails={roomDealDetails}
            scrollToDealDetails={scrollToDealDetails}
            hasImages={hasImages}
          />
        </StyledPopUp>
      )}
    </>
  )
}
